import { memo, useEffect } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { Navigation, DateUtils, ActionButton, Container } from '../components';
import { THEME, __WEB__, COLOR, FONT } from '../../constants/styles';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getCurrentEmployer } from '../../selectors/entities';
import { getEmployerInviteRequests } from '../../selectors/employers';
import { getComposedCurrentEmployerPermissions } from '../../selectors/permissions';
import { acceptOrRejectEmployerInviteRequest, resetStatus } from '../employer/actions';
import { getUsers, getInviteRequests, resendInvite, resetState } from '../staff/actions';
import * as ASSETS from '../../constants/assets';
import { BASE_SETUP_STAFF_PERMISSIONS } from '../../constants/permissions';
import { SUCCESS } from '../../actions/statusTypes';

function Row(props) {
  function handleResendInvitePress() {
    props.onResendInvitePress(props.r);
  }
  function handleInfoPress() {
    const r = props.r;

    Navigation.showModal({
      screen: 'InviteRequestDetails',
      title: r.details.name,
      passProps: {
        inviteRequest: r,
      },
    });
  }
  function handleRejectPress() {
    props.onRejectPress(props.r);
  }
  function handleAcceptPress() {
    props.onAcceptPress(props.r);
  }
  function renderInviteRequestButtons() {
    const r = props.r;
    return (
      <View style={[THEME.basicRow, THEME.rowButtons]}>
        <ActionButton
          source={ASSETS.resend}
          disabled={!r.status || !props.onResendInvitePress}
          onPress={handleResendInvitePress}
        />
        <ActionButton source={ASSETS.info} disabled={false} onPress={handleInfoPress} />
        <ActionButton
          source={ASSETS.reject}
          disabled={r.status !== null}
          onPress={handleRejectPress}
        />
        <ActionButton
          source={ASSETS.approve}
          disabled={r.status !== null}
          onPress={handleAcceptPress}
        />
      </View>
    );
  }

  const r = props.r;
  const details = r.details;
  const i = props.i;

  if (!__WEB__) {
    return (
      <View
        style={[
          THEME.basicRow,
          { backgroundColor: i % 2 === 0 ? COLOR.contrast : COLOR.altContrast },
        ]}
      >
        <View style={[THEME.rowColumn, { flex: 1 }]}>
          <Text style={THEME.rowText}>{details.name}</Text>
          <Text style={[THEME.rowText, THEME.linkStyle]}>{details.email}</Text>
        </View>
        <View style={[THEME.rowColumn, { width: 200, justifyContent: 'flex-end' }]}>
          {renderInviteRequestButtons()}
        </View>
      </View>
    );
  } else {
    return (
      <View
        style={[
          THEME.basicRow,
          { backgroundColor: i % 2 === 0 ? COLOR.contrast : COLOR.altContrast },
        ]}
      >
        <View style={[THEME.rowColumn, { flexGrow: 1, width: 200 }]}>
          <Text style={THEME.rowText}>{details.name}</Text>
        </View>
        <View style={[THEME.rowColumn, { flexGrow: 1, width: 200 }]}>
          <Text style={[THEME.rowText, THEME.linkStyle]}>{details.email}</Text>
        </View>
        <View style={[THEME.rowColumn, { width: 150 }]}>
          <Text style={THEME.rowText}>{DateUtils.formatDateStringNumeric(r.created_at)}</Text>
        </View>
        <View style={[{ paddingLeft: 15, width: 200 }]}>{renderInviteRequestButtons()}</View>
      </View>
    );
  }
}

export default memo(
  connect(
    function (state, ownProps) {
      const permissions = getComposedCurrentEmployerPermissions(state);
      const canEdit = permissions?.[BASE_SETUP_STAFF_PERMISSIONS]?.canEdit;
      return {
        employer: getCurrentEmployer(state),
        inviteRequests: getEmployerInviteRequests(state),
        pending: state.entities.employerAdmin?.users?.pending || [],
        canSendInvites: !!canEdit,
        inviteRequestStatus: state.screens.employer.inviteRequestStatus,
      };
    },
    function (dispatch) {
      return {
        actions: bindActionCreators(
          {
            acceptOrRejectEmployerInviteRequest,
            resendInvite,
            getUsers,
            getInviteRequests,
            resetState,
            resetStatus,
          },
          dispatch
        ),
      };
    }
  )(function (props) {
    const employerId = props.employer?.employerId;

    useEffect(() => {
      props.actions.resetState();
      props.actions.resetStatus();
      props.actions.getInviteRequests();
      props.actions.getUsers();
      return () => {
        props.actions.resetState();
        props.actions.resetStatus();
      };
    }, [employerId]);

    useEffect(() => {
      if (props.inviteRequestStatus === SUCCESS) {
        props.actions.getInviteRequests();
        props.actions.getUsers();
      }
    }, [props.inviteRequestStatus, employerId]);
    function acceptRequest(r) {
      const employerId = r.employer_id;
      const requestId = r.request_id;
      props.actions.acceptOrRejectEmployerInviteRequest(employerId, requestId, true);
    }
    function onAcceptPress(r, onConfirm) {
      const confirmationOpts = {
        title: 'Add this user?',
        description: `A patient record will be immediately added to the system and an invite will be sent to the user to join the app.`,
        confirm: 'Yes, Add this user',
        onConfirmed: () => {
          acceptRequest(r);
        },
      };
      props.onConfirmationOpts?.(confirmationOpts);
    }

    function rejectRequest(r) {
      const employerId = r.employer_id;
      const requestId = r.request_id;
      props.actions.acceptOrRejectEmployerInviteRequest(employerId, requestId, false);
    }
    function onRejectPress(r) {
      const confirmationOpts = {
        title: 'Reject this request?',
        description: `The user can submit a new request with their details corrected.`,
        confirm: 'Yes, Reject this request',
        onConfirmed: () => {
          rejectRequest(r);
        },
      };
      props.onConfirmationOpts?.(confirmationOpts);
    }

    function resendInvite(r) {
      props.actions.resendInvite({ email: r.email });
    }
    function onResendInvitePress(r, onConfirm) {
      const confirmationOpts = {
        title: 'Resend Invite?',
        description: `The user has been added to the system but they haven't accepted the invite to join the app. Resend the invite?`,
        confirm: 'Yes, Resend',
        onConfirmed: () => {
          resendInvite(r);
        },
      };
      props.onConfirmationOpts?.(confirmationOpts);
    }

    function renderInviteRequestRow(r, i) {
      const invitePending = props.pending.find((p) => p.email === r.email);
      const canResentInvite = invitePending && r.details.role && props.canSendInvites;
      return (
        <Row
          r={r}
          key={`${r.request_id}_${r.status}`}
          i={i}
          onAcceptPress={onAcceptPress}
          onRejectPress={onRejectPress}
          onResendInvitePress={canResentInvite ? onResendInvitePress : null}
        />
      );
    }

    if (!__WEB__) {
      return <View>{props.inviteRequests?.map(renderInviteRequestRow)}</View>;
    } else {
      return (
        <View>
          <View style={THEME.sectionHeader}>
            <View style={[THEME.sectionHeaderColumn, { flexGrow: 1, width: 200 }]}>
              <Text style={THEME.sectionHeaderText}>NAME</Text>
            </View>
            <View style={[THEME.sectionHeaderColumn, { flexGrow: 1, width: 200 }]}>
              <Text style={THEME.sectionHeaderText}>EMAIL ADDRESS</Text>
            </View>
            <View style={[THEME.sectionHeaderColumn, { width: 150 }]}>
              <Text style={THEME.sectionHeaderText}>REQUESTED ON</Text>
            </View>
            <View style={[THEME.sectionHeaderColumn, { width: 200, borderRightWidth: 0 }]}>
              <Text style={THEME.sectionHeaderText}>ACTIONS</Text>
            </View>
          </View>
          <View>{props.inviteRequests?.map(renderInviteRequestRow)}</View>
        </View>
      );
    }
  })
);

const styles = StyleSheet.create({});
